import React from 'react';
import {
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  List
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material/styles';
import NavigationItem from './NavigationItem';
import {
  getInteractiveListItemStyles,
  getListItemIconStyles,
  listItemTextStyles,
  listItemTextTypographyProps,
} from './navigationStyles';

/**
 * CollapsibleNavigationItem component for rendering a collapsible navigation item with children
 *
 * @param {Object} props - Component props
 * @param {Object} props.item - Navigation item data
 * @param {boolean} props.miniDrawer - Whether the drawer is in mini mode
 * @param {boolean} props.isDesktop - Whether the current view is desktop
 * @param {boolean} props.open - Whether the collapsible item is open
 * @param {Function} props.onToggle - Function to toggle the collapsible state
 * @param {Function} props.onItemClick - Optional click handler for child navigation items
 * @returns {JSX.Element} CollapsibleNavigationItem component
 */
const CollapsibleNavigationItem = ({
  item,
  miniDrawer,
  isDesktop,
  open,
  onToggle,
  onItemClick
}) => {
  const theme = useTheme();
  const { label, icon: Icon, children } = item;

  // Don't render collapsible items in mini drawer mode
  if (isDesktop && miniDrawer) {
    return null;
  }

  return (
    <>
      <ListItem
        onClick={onToggle}
        sx={getInteractiveListItemStyles(theme, { miniDrawer, isDesktop })}
      >
        <ListItemIcon sx={getListItemIconStyles({ miniDrawer, isDesktop })}>
          <Icon />
        </ListItemIcon>
        <ListItemText
          sx={listItemTextStyles}
          primaryTypographyProps={listItemTextTypographyProps}
          primary={label}
        />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.75 }}>
          {children.map((child) => (
            <NavigationItem
              key={child.id}
              item={child}
              miniDrawer={false} // Always show text in nested items
              isDesktop={isDesktop}
              onClick={onItemClick}
              sx={{ pl: 4 }}
            />
          ))}
        </List>
      </Collapse>
    </>
  );
};

export default CollapsibleNavigationItem;
